'use client';
import IconArrowLeft from '@/assets/icons/icon-arrow-left.svg';
import IconArrowRight from '@/assets/icons/icon-arrow-right.svg';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useEffect, useRef } from 'react';
import styles from './shares.module.scss';

import {
  OrderDirection,
  PromotionFormatEnum,
  PromotionSortField,
  useDiscountListQuery,
} from '@/graphql';
import { Link } from '@/i18n/navigation';
import dayjs from 'dayjs';
import Skeleton from 'react-loading-skeleton';
import SharesCard from '../shares-card';
import { useTranslations } from 'next-intl';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { mapEdgesToItems } from '@/helpers/maps';
import { useTranslatedData } from '@/hooks/useTranslatedData';

type Props = {
  showMoreButton?: boolean;
  title?: string;
  sliderProgressDisable?: boolean;
};

const SharedCarousel = ({
  showMoreButton,
  title,
  sliderProgressDisable = true,
}: Props) => {
  const splideRef = useRef<any>(null);
  const t = useTranslations('Components.shared-carousel');
  const languageCode = useLanguageCode();
  const { data: promotionList, loading: promotionLoad } = useDiscountListQuery({
    variables: {
      first: 10,
      languageCode: languageCode,
      where: {
        format: {
          eq: PromotionFormatEnum.PAGE,
        },
      },
      sort: {
        direction: OrderDirection.ASC,
        field: PromotionSortField.START_DATE,
      },
    },
    context: {
      requiresAuth: false,
    },
  });
  const mappedPromotions = mapEdgesToItems(promotionList?.promotions) || [];
  const translatedPromotions = useTranslatedData(mappedPromotions, ['name']);

  useEffect(() => {
    const splide = splideRef.current?.splide;

    const bar = splide?.root?.querySelector(
      `.${styles['my-slider-progress-bar']}`
    );

    // Function to update the progress bar
    const updateProgressBar = () => {
      const end = splide?.Components?.Controller.getEnd() + 1;
      const rate = Math.min((splide?.index + 1) / end, 1);
      if (bar) {
        bar.style.width = `${100 * rate}%`;
      }
    };

    // Update the progress bar immediately on mount
    updateProgressBar();

    // Add event listeners for 'mounted' and 'move' events
    splide?.on('mounted move', updateProgressBar);

    return () => {
      // Cleanup event listeners when component unmounts
      splide?.off('mounted move', updateProgressBar);
    };
  }, []);

  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };
  const goToPrevSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('<');
    }
  };

  return promotionList ? (
    <div className={styles.carouselShares}>
      <div className={styles.customHeader}>
        <h3>{title ? title : t('stock')} </h3>
        <div className={styles.customBtns}>
          <button
            type="button"
            className={styles.carouselNextButton}
            onClick={goToPrevSlide}
          >
            {' '}
            <IconArrowLeft />
          </button>
          <button
            type="button"
            className={styles.carouselNextButton}
            onClick={goToNextSlide}
          >
            {' '}
            <IconArrowRight />
          </button>
        </div>
      </div>
      <Splide
        ref={splideRef}
        options={{
          height: '400px',
          updateOnMove: true,
          perPage: 3,
          perMove: 1,
          rewind: true,
          arrows: false,
          pagination: false,
          gap: '12px',
          breakpoints: {
            1024: {
              perPage: 2,
            },
            768: {
              perPage: 2,
              gap: '8px',
            },
            580: {
              perPage: 1,
              gap: '8px',
            },
          },
        }}
        hasTrack={false}
        style={{ padding: '0' }}
      >
        <SplideTrack style={{ paddingRight: '40px !important' }}>
          {promotionLoad
            ? Array.from({ length: 5 }).map((_, index) => (
                <SplideSlide key={index}>
                  <div className={`${styles.skeletonPopular}`}>
                    <Skeleton />
                  </div>
                </SplideSlide>
              ))
            : translatedPromotions.map(shares => (
                <SplideSlide key={shares.id}>
                  <SharesCard
                    image={shares.image}
                    title={shares.name || '111'}
                    day={dayjs(shares.endDate).diff(
                      dayjs(shares.startDate),
                      'day'
                    )}
                    slug={shares.slug}
                    id={shares.id}
                  />
                </SplideSlide>
              ))}
        </SplideTrack>
        {sliderProgressDisable ? (
          <div className={styles['my-slider-progress']}>
            <div className={styles['my-slider-progress-bar']}></div>
          </div>
        ) : null}
      </Splide>
      {showMoreButton && (
        <Link href="/promotions">
          <div className={styles.moreCards}>{t('all-stocks')}</div>
        </Link>
      )}
    </div>
  ) : null;
};

export default SharedCarousel;
